/**
 * Help (`/help`, inside Layout): Guardian's in-app guide, in the Carbon type
 * of mockup carbon/01 (blueprint §5.2 `public`).
 *
 * Layout: the breadcrumb (from Layout) names the page; below it, a 256px
 * column with the search field and the section list (a side-nav look: 14px/600
 * items, the selected one on layer-02 with the 3px Soft Green bar), then the
 * active section. Its title is the view's one h1, its description the lede.
 * Below 1056px the section list folds behind a "Browse sections" disclosure.
 * <main> stays the only scroller: nothing here sets a viewport height or its
 * own scroll container, and wide tables scroll inside their own box.
 *
 * Blocks map onto primitives: tables are DataTables (stacked rows on phones),
 * accordions are one Carbon Accordion per run of consecutive items, callouts
 * are neutral notes (no hue: tips, warnings and confirmations alike), and the
 * status cards and feature grid are condensed-grid tiles. Status and severity
 * carry no color under Carbon, so the reference tables name the severity
 * shapes instead of colors.
 *
 * Copy is guarded by ui-copy-lint (this file is registered).
 */
import { useState, useEffect, useRef, useCallback, Fragment, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  Accordion,
  Button,
  ButtonLink,
  CodeSnippet,
  CondensedGrid,
  DataTable,
  IconArrowRight,
  IconChevronDown,
  IconExternal,
  TableSearch,
  Tile,
  TileHeader,
  type AccordionItem,
  type DataColumn,
} from '@/components/carbon'
import { DocList, DocNote } from './docs/docs-ui'

// ─── Types ───────────────────────────────────────────────────────────

interface HelpSection {
  id: string
  title: string
  description: string
  content: ContentBlock[]
}

type ContentBlock =
  | { type: 'heading'; level: 2 | 3; text: string; id?: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered?: boolean; items: string[] }
  | { type: 'callout'; variant: 'info' | 'tip' | 'warning' | 'success'; title?: string; text: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'accordion'; title: string; content: ContentBlock[] }
  | { type: 'status-card'; items: { status: string; description: string; action: string }[] }
  | { type: 'feature-grid'; items: { title: string; description: string; icon?: string }[] }
  | { type: 'divider' }

// ─── Content Data ────────────────────────────────────────────────────

const helpSections: HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'New to Guardian? Start here for a complete overview.',
    content: [
      { type: 'heading', level: 2, text: 'What is iuvo Guardian?', id: 'what-is-guardian' },
      { type: 'paragraph', text: 'Guardian continuously checks your Microsoft 365 configuration against your baselines and the frameworks you choose, then flags what changed and what to fix.' },
      { type: 'heading', level: 3, text: 'Core Capabilities' },
      { type: 'feature-grid', items: [
        { title: 'Configuration Drift Detection', description: 'Scheduled scans of Microsoft 365 settings against your approved baselines', icon: 'drift' },
        { title: 'Multi-Framework Compliance', description: 'Policies mapped to CISA SCuBA, NIST, MCSB, EIDSCA and Maester, evaluated against your tenant', icon: 'compliance' },
        { title: 'User Analytics', description: 'License utilization, MFA adoption, phish-resistant authentication, and inactive user detection', icon: 'users' },
        { title: 'OAuth App Monitoring', description: 'AI-enriched risk classification of third-party OAuth applications in your tenant', icon: 'apps' },
        { title: 'Executive Reporting', description: 'PDF/HTML reports with AI-generated executive summaries and compliance trends', icon: 'reports' },
        { title: 'Security Posture Hub', description: 'Unified dashboard combining Best Practice Analyzer, Secure Score, and SCuBA assessments', icon: 'security' },
      ]},
      { type: 'heading', level: 2, text: 'Your First Steps', id: 'first-steps' },
      { type: 'list', ordered: true, items: [
        '**Review the Command Center.** See your tenant\'s security health, active alerts and compliance scores.',
        '**Explore the Security Center.** View your Guardian Score and Best Practice Analyzer results across every compliance framework you have starred.',
        '**Check User Analytics.** Review MFA adoption, phish-resistant auth coverage and license utilization.',
        '**Run a configuration scan.** Navigate to Tenant Management, select your tenant and click Scan Now.',
        '**Review detected drift.** Check the Baseline Compliance page for any configuration changes.',
        '**Generate a report.** Use Executive Report to create a PDF/HTML summary for stakeholders.',
      ]},
      { type: 'heading', level: 2, text: 'Key Concepts', id: 'key-concepts' },
      { type: 'table', headers: ['Concept', 'Description'], rows: [
        ['Configuration Drift', 'When your actual M365 settings differ from your approved baselines'],
        ['Guardian Score', 'Aggregated from your starred compliance frameworks (A-F letter grade)'],
        ['Compliance Policies', 'Security rules that evaluate your tenant configuration against compliance frameworks'],
        ['Waiver', 'An exception for a compliance violation. Can be permanent, temporary (auto-expiring) or not applicable.'],
        ['Baseline', 'Your approved "gold standard" M365 configuration that Guardian monitors against'],
        ['Phish-Resistant MFA', 'Strong authentication methods: FIDO2 security keys, Windows Hello for Business, certificate-based auth'],
      ]},
      { type: 'callout', variant: 'tip', title: 'Quick Tip', text: 'Use the sidebar navigation to jump between Guardian\'s key areas. The Command Center is your starting point for security monitoring.' },
    ]
  },
  {
    id: 'command-center',
    title: 'Command Center',
    description: 'Your operations dashboard for current tenant health.',
    content: [
      { type: 'heading', level: 2, text: 'Dashboard Overview', id: 'dashboard-overview' },
      { type: 'paragraph', text: 'The Command Center is your primary operations hub, providing an overview of your Microsoft 365 security posture as of the last scan. It aggregates data from drift detection, compliance evaluation and user analytics into a single view.' },
      { type: 'heading', level: 3, text: 'Key Metrics' },
      { type: 'list', items: [
        '**Guardian Score.** Your overall compliance score with letter grade (A-F) based on starred frameworks.',
        '**Active drift count.** Number of unresolved configuration drift items by severity.',
        '**Compliance violations.** Current violations across all evaluated frameworks.',
        '**User risk indicators.** MFA adoption rate, inactive users and license utilization.',
      ]},
      { type: 'heading', level: 3, text: 'Interactive Features' },
      { type: 'list', items: [
        '**Clickable metric cards.** Click any metric to navigate directly to the detailed view with appropriate filters applied.',
        '**Recent activity feed.** Drift detections, acceptances and compliance changes.',
        '**Drift trends.** A 30-day visualization of drift detection and resolution trends.',
        '**Top risks.** A severity-ranked list of your most critical security findings.',
      ]},
      { type: 'callout', variant: 'info', text: 'Dashboard data updates after each scan. You can also trigger manual scans from Tenant Management for immediate updates.' },
    ]
  },
  {
    id: 'baseline-compliance',
    title: 'Baseline Compliance',
    description: 'Configuration drift detection and baseline management.',
    content: [
      { type: 'heading', level: 2, text: 'How Drift Detection Works', id: 'how-drift-works' },
      { type: 'paragraph', text: 'Guardian continuously exports your current M365 configuration and compares it against your approved baseline. When differences are detected, they are flagged as configuration drift with AI-generated analysis and severity scoring.' },
      { type: 'heading', level: 3, text: 'Detection Pipeline' },
      { type: 'list', ordered: true, items: [
        'Scheduled or manual scan is triggered',
        'Guardian exports your current M365 configuration',
        'Current configuration is compared against your approved baseline',
        'Differences are identified and categorized by component type and severity',
        'AI analysis generates human-readable descriptions and remediation guidance',
        'Results appear on the Baseline Compliance page for review',
      ]},
      { type: 'heading', level: 2, text: 'Reviewing Drift', id: 'reviewing-drift' },
      { type: 'paragraph', text: 'Each drift item shows the affected component, property, before/after values, AI-generated description, severity level and recommended action. Expand any drift row to see its detail in place.' },
      { type: 'heading', level: 3, text: 'Drift Detail' },
      { type: 'list', items: [
        '**Before/After comparison.** See exactly what changed with highlighted differences.',
        '**AI analysis.** A human-readable explanation of the change and its security implications.',
        '**Severity assessment.** A composite severity informed by the property changed, the compliance impact of the change and AI risk analysis.',
        '**Remediation guidance.** Recommended steps to address the drift.',
        '**Audit history.** The complete timeline of status changes and decisions.',
      ]},
      { type: 'heading', level: 2, text: 'How Severity Is Determined', id: 'drift-severity-scoring' },
      { type: 'paragraph', text: 'Drift severity is the strictest result from three independent assessments. Severity only ever escalates upward through the pipeline so that a high-risk change can never be hidden by a softer score from another layer.' },
      { type: 'list', ordered: true, items: [
        '**Property-level baseline.** Some properties are inherently more sensitive than others (for example, a Conditional Access policy state change is treated more seriously than a display-name edit). Each property carries a baseline severity that establishes the floor.',
        '**Compliance impact.** Guardian re-evaluates your tenant against its compliance frameworks after the change is detected. If the drift introduces a new violation, the drift\'s severity is elevated to match the criticality of the broken requirement (Critical for SHALL-level controls, High for SHOULD-level, Medium for MAY-level). Drifts that resolve a prior violation are noted but do not lower severity.',
        '**AI risk analysis.** Guardian\'s AI reviews the full change in context (the resource type, the values involved, related security signals) and may elevate severity further if it detects a risk pattern the first two layers missed.',
      ]},
      { type: 'callout', variant: 'tip', title: 'Why drift and compliance are connected', text: 'A configuration change that breaks a critical compliance control should never appear as a low-severity drift. By tying compliance evaluation directly into severity scoring, Guardian ensures that drifts which create real audit or framework gaps are surfaced at the seriousness they deserve. You do not have to cross-reference Best Practice by hand.' },
      { type: 'heading', level: 2, text: 'Making Decisions', id: 'making-decisions' },
      { type: 'table', headers: ['Action', 'When to Use', 'What Happens'], rows: [
        ['Accept', 'Legitimate business change that should become the new standard', 'Baseline is updated; drift status becomes Remediated after next scan'],
        ['Reject', 'Unauthorized or incorrect change', 'Drift is flagged; your iuvo client team is notified. If change persists, it reappears as New'],
        ['Ignore', 'Acceptable temporary deviation', 'Acknowledged but baseline unchanged. May reappear if value changes further'],
      ]},
      { type: 'heading', level: 2, text: 'Bulk Operations', id: 'bulk-operations' },
      { type: 'paragraph', text: 'Select multiple drift items using checkboxes to accept them in bulk. The system will show an impact warning before processing, including any additional drifts that may be included for resource-level updates.' },
      { type: 'callout', variant: 'warning', title: 'Resource-Level Updates', text: 'Accepting multiple drifts at once may include additional related drifts for complex resources. Always review the impact summary before confirming.' },
      { type: 'heading', level: 2, text: 'Baseline Management', id: 'baseline-management' },
      { type: 'list', items: [
        '**Change tracker.** Monitor the status of baseline updates created from accepted drifts.',
        '**Baseline export.** Download your current baseline configuration.',
        '**Automatic updates.** Accepted drifts trigger baseline updates.',
      ]},
    ]
  },
  {
    id: 'security-center',
    title: 'Security Center',
    description: 'Unified security assessment hub with multiple evaluation tools.',
    content: [
      { type: 'heading', level: 2, text: 'Security Center Overview', id: 'security-overview' },
      { type: 'paragraph', text: 'The Security Center consolidates all security assessments into a single hub with five tabs: Overview, Best Practice Analyzer, Secure Score, SCuBA Deep Scan, and App Permissions.' },
      { type: 'heading', level: 2, text: 'Overview Tab', id: 'overview-tab' },
      { type: 'list', items: [
        '**Guardian Score dashboard.** Your score with A-F letter grade.',
        '**Framework status cards.** Per-framework compliance scores and pass/fail counts.',
        '**Recent violations.** Latest compliance findings ranked by severity.',
        '**Quick navigation.** Links to detailed assessments in other tabs.',
      ]},
      { type: 'heading', level: 2, text: 'Best Practice Analyzer', id: 'bpa' },
      { type: 'paragraph', text: 'Best Practice evaluates your Microsoft 365 tenant against policies spanning Identity, Exchange, Defender, Teams and SharePoint, each mapped to CISA SCuBA, NIST, MCSB, EIDSCA and Maester.' },
      { type: 'heading', level: 3, text: 'Framework Cards' },
      { type: 'paragraph', text: 'Each framework card shows the number of passing, failing, and waived policies. Click a framework card to filter the violations table to that specific framework.' },
      { type: 'heading', level: 3, text: 'Violations Table' },
      { type: 'list', items: [
        '**Policy ID.** Unique identifier (e.g., GUARD.IAM.001).',
        '**Criticality.** SHALL (Critical), SHOULD (High) or MAY (Medium).',
        '**Status.** Pass, Fail or Waived.',
        '**Frameworks.** Which frameworks this policy maps to.',
        '**Remediation.** Guidance on how to address the violation.',
      ]},
      { type: 'callout', variant: 'tip', title: 'All Controls View', text: 'Use the "All Controls" view to see every evaluated policy including passing ones, not just violations.' },
      { type: 'heading', level: 2, text: 'Microsoft Secure Score', id: 'secure-score' },
      { type: 'paragraph', text: 'Integrates with the Microsoft Secure Score API to display your tenant\'s score, improvement actions, and estimated point gains. Use the Sync button to refresh data from Microsoft.' },
      { type: 'heading', level: 2, text: 'SCuBA Deep Scan', id: 'scuba' },
      { type: 'paragraph', text: 'Performs a CISA SCuBA (Secure Cloud Business Applications) baseline evaluation against federal M365 security requirements per BOD 25-01.' },
      { type: 'heading', level: 2, text: 'App Permissions', id: 'app-permissions' },
      { type: 'paragraph', text: 'Monitors OAuth applications registered in your tenant for security risks. Features include:' },
      { type: 'list', items: [
        '**Risk classification.** Applications rated as Critical, High, Medium or Low risk based on permission tiers.',
        '**AI risk analysis.** AI-generated risk summaries and remediation recommendations.',
        '**Stale app detection.** Identifies applications with no sign-in activity for 90+ days.',
        '**First-party filtering.** Microsoft-owned apps are deprioritized in risk assessments.',
        '**Permission tier breakdown.** Detailed view of each app\'s permission scope and risk level.',
      ]},
    ]
  },
  {
    id: 'user-analytics',
    title: 'User Analytics',
    description: 'MFA adoption, license utilization, and user risk insights.',
    content: [
      { type: 'heading', level: 2, text: 'User Analytics Overview', id: 'analytics-overview' },
      { type: 'paragraph', text: 'The User Analytics page covers your Microsoft 365 user population: authentication methods, license utilization and sign-in activity.' },
      { type: 'heading', level: 2, text: 'MFA Adoption', id: 'mfa-adoption' },
      { type: 'list', items: [
        '**Overall MFA rate.** Percentage of users with MFA enabled.',
        '**MFA method breakdown.** Distribution of authentication methods across your user population.',
        '**Users without MFA.** List of users who have not enrolled in multi-factor authentication.',
      ]},
      { type: 'heading', level: 2, text: 'Phish-Resistant Authentication', id: 'phish-resistant' },
      { type: 'paragraph', text: 'Guardian specifically tracks adoption of phish-resistant MFA methods, which are considered the strongest form of authentication:' },
      { type: 'table', headers: ['Method', 'Description'], rows: [
        ['FIDO2 Security Keys', 'Hardware security keys (e.g., YubiKey) that use public key cryptography'],
        ['Windows Hello for Business', 'Biometric or PIN-based authentication tied to the device TPM'],
        ['Certificate-Based Auth', 'X.509 certificate authentication using smart cards or virtual smart cards'],
      ]},
      { type: 'callout', variant: 'info', text: 'Phish-resistant MFA methods are immune to common phishing attacks because they bind authentication to the legitimate service. Attackers cannot relay or replay the credential.' },
      { type: 'heading', level: 2, text: 'License Utilization', id: 'license-utilization' },
      { type: 'list', items: [
        '**Active vs. inactive users.** Identify licensed users who haven\'t signed in recently.',
        '**Inactivity threshold.** Configurable per-tenant (default: 90 days).',
        '**License waste analysis.** Shared mailbox detection to identify licenses that can be reclaimed.',
      ]},
      { type: 'heading', level: 2, text: 'Trend Analysis', id: 'trend-analysis' },
      { type: 'paragraph', text: 'Historical charts show week-over-week trends for MFA adoption, phish-resistant auth coverage, and user activity. Use these trends to track the effectiveness of your security initiatives over time.' },
      { type: 'heading', level: 2, text: 'CSV Export', id: 'csv-export' },
      { type: 'paragraph', text: 'Export the "Users Requiring Attention" table to CSV for offline analysis, reporting, or integration with other tools.' },
    ]
  },
  {
    id: 'compliance-frameworks',
    title: 'Compliance Frameworks',
    description: 'The security frameworks Guardian evaluates against.',
    content: [
      { type: 'heading', level: 2, text: 'Supported Frameworks', id: 'supported-frameworks' },
      { type: 'paragraph', text: 'Guardian evaluates your Microsoft 365 tenant against these security frameworks, with policies spanning Identity, Exchange, Defender, Teams and SharePoint.' },
      { type: 'table', headers: ['Framework', 'Focus'], rows: [
        ['CISA SCuBA', 'Federal M365 security baselines per BOD 25-01 (Secure Cloud Business Applications).'],
        ['NIST SP 800-53 Rev 5', 'Security and privacy controls for information systems.'],
        ['NIST CSF 2.0', 'Cybersecurity Framework for managing and reducing cybersecurity risk.'],
        ['Microsoft MCSB', 'Microsoft Cloud Security Benchmark (Microsoft\'s recommended cloud security controls).'],
        ['EIDSCA', 'Entra ID Security Config Analyzer, focused on identity security configuration.'],
        ['Maester', 'M365 Security Testing Framework, with community-driven M365 security tests.'],
      ]},
      { type: 'heading', level: 2, text: 'Policy Classification', id: 'policy-classification' },
      { type: 'heading', level: 3, text: 'Criticality Levels' },
      { type: 'table', headers: ['Level', 'Keyword', 'Meaning'], rows: [
        ['Critical', 'SHALL', 'Mandatory. Non-compliance is a significant security risk.'],
        ['High', 'SHOULD', 'Strongly recommended. Implement unless there is a documented exception.'],
        ['Medium', 'MAY', 'Optional. Consider implementing based on your risk tolerance.'],
      ]},
      { type: 'heading', level: 3, text: 'Workloads' },
      { type: 'list', items: [
        '**Identity (Entra ID).** Conditional Access, MFA, PIM, authentication methods and identity protection.',
        '**Exchange Online.** Mail flow rules, anti-spam, anti-malware, DLP and audit logging.',
        '**Microsoft Defender.** Safe Links, Safe Attachments, anti-phishing and threat protection.',
        '**Teams.** External access, guest policies, meeting settings and messaging policies.',
        '**SharePoint Online.** Sharing settings, access control, site creation and OneDrive policies.',
      ]},
      { type: 'heading', level: 2, text: 'Guardian Score', id: 'guardian-score' },
      { type: 'paragraph', text: 'Your Guardian Score is calculated from your starred frameworks. It provides an A-F letter grade alongside a numeric percentage. You can customize which frameworks contribute to your score by starring/unstarring them in the Security Center.' },
      { type: 'callout', variant: 'tip', title: 'Framework Flexibility', text: 'You can assign or unassign frameworks based on your organization\'s regulatory requirements. Only starred frameworks contribute to your Guardian Score.' },
    ]
  },
  {
    id: 'executive-reports',
    title: 'Executive Reports',
    description: 'Generate PDF/HTML compliance reports for stakeholders.',
    content: [
      { type: 'heading', level: 2, text: 'Report Generation', id: 'report-generation' },
      { type: 'paragraph', text: 'The Executive Report feature generates professional compliance reports suitable for executive stakeholders, board presentations, and MSP client distribution.' },
      { type: 'heading', level: 3, text: 'Report Contents' },
      { type: 'list', items: [
        '**Guardian Score dashboard.** Current score with A-F letter grade and trend arrows.',
        '**AI executive summary.** An AI-generated 3-4 paragraph narrative summary of your compliance posture.',
        '**Framework deep dives.** Per-framework compliance scores with category breakdowns.',
        '**Compliance trends.** Score history and improvement trajectory over configurable periods (30/60/90 days).',
        '**Violation summary.** Key findings ranked by severity with remediation status.',
        '**Metric snapshots.** Violations resolved, drift remediated and MFA adoption rates.',
      ]},
      { type: 'heading', level: 3, text: 'Export Options' },
      { type: 'list', items: [
        '**PDF.** A professional branded document for printing and sharing.',
        '**HTML.** An interactive web-based report for browser viewing.',
      ]},
      { type: 'callout', variant: 'info', text: 'Reports are generated on-demand from your current compliance data. The AI executive summary is generated while you wait.' },
    ]
  },
  {
    id: 'drift-management',
    title: 'Drift Management',
    description: 'Acceptance workflows, bulk operations and baseline updates.',
    content: [
      { type: 'heading', level: 2, text: 'Drift Lifecycle', id: 'drift-lifecycle' },
      { type: 'paragraph', text: 'Every detected drift follows a defined lifecycle from detection through resolution. Understanding this lifecycle helps you manage drift efficiently.' },
      { type: 'status-card', items: [
        { status: 'New', description: 'Recently detected configuration change', action: 'Review and decide: Accept, Reject or Ignore' },
        { status: 'Pending', description: 'Accepted and queued for batch processing', action: 'None. Included in the next batch update.' },
        { status: 'PendingApproval', description: 'Requires additional approval before processing', action: 'Manager or designated approver must review' },
        { status: 'Approved', description: 'Accepted change. Baseline update in progress.', action: 'None. Baseline update is in progress.' },
        { status: 'Rejected', description: 'Unauthorized or incorrect change', action: 'Investigate and manually fix in M365. Your iuvo team is notified.' },
        { status: 'AwaitingRemediation', description: 'Processed, waiting for next scan confirmation', action: 'None. Allow 24-48 hours for the next scan cycle.' },
        { status: 'Remediated', description: 'Resolved. No longer detected by the scan.', action: 'None. The change is now part of your standard config.' },
        { status: 'Ignored', description: 'Acknowledged but baseline not updated', action: 'Monitor occasionally. Returns as New if value changes.' },
      ]},
      { type: 'heading', level: 2, text: 'Automatic Reconciliation', id: 'reconciliation' },
      { type: 'paragraph', text: 'Every scan reconciles drift statuses:' },
      { type: 'list', items: [
        '**Approved → Remediated.** When the accepted drift is no longer detected.',
        '**Rejected → New.** If the rejected change reappears in a scan.',
        '**Remediated → New.** If a previously fixed drift returns (regression detection).',
      ]},
      { type: 'heading', level: 2, text: 'Batch Processing', id: 'batch-processing' },
      { type: 'paragraph', text: 'Individual acceptances are queued for later batch processing, combining multiple changes into a single baseline update per tenant. Bulk acceptances trigger immediate batch updates.' },
      { type: 'heading', level: 3, text: 'Property-Level vs. Resource-Level Updates' },
      { type: 'table', headers: ['Type', 'When Used', 'Impact'], rows: [
        ['Property-Level', 'Individual settings changed (permissions, timeouts)', 'Only the specific changed property is updated. Cleaner and more granular.'],
        ['Resource-Level', 'Complex nested objects, new resources, interdependent settings', 'The entire configuration block is updated. May include related drifts.'],
      ]},
    ]
  },
  {
    id: 'waivers',
    title: 'Waivers & Exceptions',
    description: 'Managing compliance exceptions and policy waivers.',
    content: [
      { type: 'heading', level: 2, text: 'Waiver System', id: 'waiver-system' },
      { type: 'paragraph', text: 'The waiver system allows you to create documented exceptions for compliance violations when a policy does not apply to your environment or when you have an accepted risk.' },
      { type: 'heading', level: 3, text: 'Waiver Types' },
      { type: 'table', headers: ['Type', 'Duration', 'Use Case'], rows: [
        ['Permanent', 'No expiry', 'Policy does not apply to your organization\'s operating model'],
        ['Temporary', 'Auto-expires on set date', 'Accepted risk with a planned remediation date'],
        ['Not Applicable', 'No expiry', 'Policy evaluates a feature/service your organization does not use'],
      ]},
      { type: 'heading', level: 2, text: 'Creating Waivers', id: 'creating-waivers' },
      { type: 'list', ordered: true, items: [
        'Navigate to the **Security Center → Best Practice Analyzer**',
        'Find the violation you want to waive in the violations table',
        'Click the **Waive** button on the violation row',
        'Select the waiver type (Permanent, Temporary, or Not Applicable)',
        'For Temporary waivers, set the expiration date',
        'Add a justification explaining why this waiver is appropriate',
        'Click **Create Waiver** to save',
      ]},
      { type: 'heading', level: 2, text: 'Waiver Impact on Scoring', id: 'waiver-scoring' },
      { type: 'callout', variant: 'info', text: 'Waived violations are excluded from compliance score calculations. This means your Guardian Score and per-framework scores will reflect only the policies that actively apply to your environment.' },
      { type: 'heading', level: 2, text: 'Auto-Expiration', id: 'auto-expiration' },
      { type: 'paragraph', text: 'Temporary waivers deactivate after their expiration date. When a waiver expires, the underlying violation becomes active again and will appear in your compliance results. All waiver lifecycle events are logged in the audit trail.' },
    ]
  },
  {
    id: 'status-reference',
    title: 'Status Reference',
    description: 'Quick reference for drift statuses, severities and timelines.',
    content: [
      { type: 'heading', level: 2, text: 'Drift Status Lifecycle', id: 'status-lifecycle' },
      { type: 'table', headers: ['Status', 'Description', 'Auto Behavior'], rows: [
        ['New', 'Just detected, awaiting review', 'None'],
        ['Pending', 'Queued for batch processing', 'Batched with other acceptances'],
        ['PendingApproval', 'Needs additional approval', 'None until approved'],
        ['Approved', 'Accepted, baseline updating', 'Auto-updates baseline'],
        ['Rejected', 'Unacceptable change', 'Reappears as New if detected again'],
        ['Remediated', 'No longer detected', 'Returns as New if drift regresses'],
        ['Ignored', 'Acknowledged, no update', 'Reappears as New if value changes'],
        ['AwaitingRemediation', 'Processed, awaiting scan', 'Auto-updates to Remediated'],
      ]},
      { type: 'heading', level: 2, text: 'Severity Levels', id: 'severity-levels' },
      { type: 'table', headers: ['Severity', 'Shape', 'Meaning'], rows: [
        ['Critical', 'Octagon, in coral', 'Immediate security risk. MFA weakening, admin role changes, external sharing escalation.'],
        ['High', 'Triangle', 'Significant impact likely. Security policy changes, access control modifications.'],
        ['Medium', 'Diamond', 'Moderate impact possible. Configuration changes with indirect security implications.'],
        ['Low', 'Square', 'Minimal impact expected. Cosmetic or low-risk configuration changes.'],
      ]},
      { type: 'heading', level: 2, text: 'Timeline Expectations', id: 'timeline' },
      { type: 'table', headers: ['Stage', 'Expected Duration'], rows: [
        ['Accept → Processing', 'Usually within 1 hour during business hours'],
        ['Processing → Baseline Update', '15-30 minutes for baseline integration'],
        ['Next Scan → Status Update', '24-48 hours to confirm change took effect'],
      ]},
    ]
  },
  {
    id: 'administration',
    title: 'Administration',
    description: 'Managing organizations, tenants, and user access.',
    content: [
      { type: 'heading', level: 2, text: 'Organization Management', id: 'organization-management' },
      { type: 'paragraph', text: 'Organizations are the top-level access accounts in Guardian. Each one represents a company or team that signs in from a specific Microsoft directory. Before users from a new company can access Guardian, their organization must appear on this page and be marked active.' },
      { type: 'paragraph', text: 'Organizations in "Pending" status have completed the setup consent step and are waiting for iuvo review. Approving one grants their team access immediately. Deactivating one cuts off new sign-ins; existing sessions wind down on their own.' },
      { type: 'heading', level: 2, text: 'Monitored Tenants', id: 'monitored-tenants' },
      { type: 'paragraph', text: 'Monitored tenants are the customer tenants Guardian scans for security drift. Each one connects a Microsoft directory to an access record, and controls how Guardian signs in to read its configuration.' },
      { type: 'paragraph', text: 'Before adding a tenant, confirm an access record already exists for the directory. The Tenant ID field on the form checks for you and warns when no access record is found, so you can create one without leaving the page.' },
      { type: 'heading', level: 2, text: 'User Management', id: 'user-management' },
      { type: 'paragraph', text: 'Users appear on this page after their first sign-in. Assigning a user to a tenant lets them view that tenant\'s security data. A user\'s role applies across all their assigned tenants. A Viewer can read data but cannot make changes anywhere.' },
    ]
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Common issues and their solutions.',
    content: [
      { type: 'heading', level: 2, text: 'Authentication & Access', id: 'auth-issues' },
      { type: 'accordion', title: 'Can\'t login or see "Access Denied"', content: [
        { type: 'list', ordered: true, items: [
          'Clear browser cache and cookies',
          'Try an incognito/private browsing window',
          'Sign out of all Microsoft accounts, then sign back in',
          'Ensure you\'re using the correct Microsoft Entra ID account',
          'Contact your administrator to verify your user role assignment',
        ]},
      ]},
      { type: 'accordion', title: 'Can\'t see certain tenants', content: [
        { type: 'paragraph', text: 'Tenant visibility is controlled by user-tenant associations. If you were recently granted access, sign out and back in. Permission changes can take 15-30 minutes to propagate.' },
      ]},
      { type: 'heading', level: 2, text: 'Data & Dashboard', id: 'data-issues' },
      { type: 'accordion', title: 'Dashboard shows old or missing data', content: [
        { type: 'list', ordered: true, items: [
          'Hard refresh the page (Ctrl+F5 on Windows, Cmd+Shift+R on Mac)',
          'Check in Tenant Management that recent scans completed',
          'Run a manual scan to force fresh data collection',
          'Dashboard data refreshes within five minutes.',
        ]},
      ]},
      { type: 'accordion', title: '"No Data Available" messages', content: [
        { type: 'paragraph', text: 'This typically means no scans have run yet for the selected tenant, or the most recent scan failed. Go to Tenant Management, select the tenant, and click "Scan Now" to initiate data collection. Initial scans take 15-30 minutes.' },
      ]},
      { type: 'heading', level: 2, text: 'Drift & Compliance', id: 'drift-issues' },
      { type: 'accordion', title: 'Approved drift still shows "AwaitingRemediation"', content: [
        { type: 'callout', variant: 'success', title: 'This is normal.', text: 'Once accepted, allow 24-48 hours for processing and the next scan cycle. The status updates to "Remediated" once the next scan confirms the drift is resolved. Contact support only if stuck for more than 48 hours.' },
      ]},
      { type: 'accordion', title: 'Can\'t accept or reject drifts', content: [
        { type: 'list', items: [
          'Verify you have the **Approver** or **Admin** role for this tenant',
          'Refresh the page and try again',
          'Try a different browser to rule out browser-specific issues',
          'If bulk operations fail, try processing items individually',
        ]},
      ]},
      { type: 'accordion', title: 'No drift detected despite known changes', content: [
        { type: 'list', items: [
          'Changes may not be reflected until the next scan cycle',
          'Verify the changed items are covered by your baseline configuration',
          'Run a manual scan to force immediate detection',
          'Some M365 changes take time to propagate through Microsoft\'s APIs',
        ]},
      ]},
      { type: 'heading', level: 2, text: 'Scanning Issues', id: 'scan-issues' },
      { type: 'accordion', title: 'Scan never completes or appears hung', content: [
        { type: 'paragraph', text: 'Complex environments can take 45+ minutes to scan. Check the scan job status for progress updates. If a scan appears stuck for more than 2 hours, contact your administrator. Try scheduling scans during off-peak hours for better performance.' },
      ]},
      { type: 'heading', level: 2, text: 'When to Contact Support', id: 'contact-support' },
      { type: 'callout', variant: 'warning', title: 'Contact support immediately for:', text: 'Security concerns (suspicious unauthorized changes), complete system unavailability, data corruption, or persistent authentication failures. Include the exact error message, steps to reproduce, browser/OS info, tenant details, and screenshots.' },
    ]
  },
  {
    id: 'faq',
    title: 'FAQ',
    description: 'Frequently asked questions and quick answers.',
    content: [
      { type: 'heading', level: 2, text: 'General', id: 'faq-general' },
      { type: 'accordion', title: 'What is configuration drift?', content: [
        { type: 'paragraph', text: 'Configuration drift occurs when your Microsoft 365 environment\'s actual settings differ from your approved baseline. This can happen due to manual changes by administrators, third-party tools, Microsoft updates, or unauthorized modifications.' },
      ]},
      { type: 'accordion', title: 'How often does Guardian scan my environment?', content: [
        { type: 'paragraph', text: 'On a tenant watched through Microsoft\'s Tenant Configuration Management, Microsoft checks up to about 200 of your most critical settings every six hours, and Guardian picks up any change within the hour after. Everything else it covers is compared in a full configuration capture every 2 to 30 days, depending on the size of your tenant, or later when the tenant\'s monthly snapshot allowance runs short. That capture also catches settings that were added or removed. SharePoint, OneDrive and anything Guardian scans directly follow the tenant\'s scan schedule (Manual, Daily or Weekly), and so does everything on a tenant not watched through Tenant Configuration Management. Your administrator can start a scan at any time. While a schedule is on, user analytics refresh about every 12 hours.' },
      ]},
      { type: 'accordion', title: 'Will Guardian change my M365 settings on its own?', content: [
        { type: 'callout', variant: 'success', text: 'No. Guardian doesn\'t change your Microsoft 365 policies or settings. It detects drift and evaluates compliance. In your tenant, Guardian writes only its own Tenant Configuration Management objects: it creates, updates and deletes its configuration monitors, and creates configuration snapshots. Accepting a change updates Guardian\'s baseline, not your settings.' },
      ]},
      { type: 'heading', level: 2, text: 'Compliance', id: 'faq-compliance' },
      { type: 'accordion', title: 'What is the Guardian Score?', content: [
        { type: 'paragraph', text: 'The Guardian Score (0-100% with A-F letter grade) is calculated from your starred compliance frameworks. It aggregates pass/fail results from policy evaluations. You can customize which frameworks contribute by starring/unstarring them in the Security Center.' },
      ]},
      { type: 'accordion', title: 'Can I choose which frameworks to evaluate against?', content: [
        { type: 'paragraph', text: 'Yes. Guardian supports CISA SCuBA, NIST SP 800-53, NIST CSF 2.0, Microsoft MCSB, EIDSCA, and Maester. You can assign or unassign frameworks based on your regulatory requirements. Only starred frameworks contribute to your Guardian Score.' },
      ]},
      { type: 'accordion', title: 'What happens when I waive a violation?', content: [
        { type: 'paragraph', text: 'Waived violations are excluded from compliance score calculations. Permanent waivers remain indefinitely. Temporary waivers auto-expire on the set date, causing the violation to become active again. All waiver events are logged in the audit trail.' },
      ]},
      { type: 'heading', level: 2, text: 'Security & Privacy', id: 'faq-security' },
      { type: 'accordion', title: 'Does Guardian read my email, files or passwords?', content: [
        { type: 'callout', variant: 'info', text: 'No. Guardian reads your configuration (policies, rules and admin settings) and directory details about your users, such as names, sign-in names, job titles, licences, last sign-in, MFA registration and admin roles, plus who changed a setting, from your Entra audit log. It doesn\'t read email or file content, Teams chats or passwords. Guardian\'s Sites.FullControl.All permissions would technically allow reading SharePoint and OneDrive files. Microsoft offers no narrower permission that can export SharePoint and OneDrive settings, and that export is all Guardian uses them for.' },
      ]},
      { type: 'accordion', title: 'Why do some permissions say "ReadWrite"?', content: [
        { type: 'paragraph', text: 'Most of Guardian\'s permissions are read-only, and a few are read-write. Guardian uses write access only for its own Tenant Configuration Management objects in your tenant: the configuration monitors it creates, updates and deletes, and the configuration snapshots it creates. It doesn\'t use the other read-write permissions to change anything. Some are read-write because Microsoft offers no read-only way to export those settings. Permissions & security in the documentation (/docs/permissions) has more on Guardian\'s permissions.' },
      ]},
      { type: 'accordion', title: 'Is my data stored outside my tenant?', content: [
        { type: 'paragraph', text: 'Yes. Guardian stores what it collects in iuvo\'s Azure environment: your configuration and its snapshots, drift history, and the directory details about your users listed above. Some baseline history also goes to iuvo\'s Azure DevOps repository. Guardian\'s database and storage are encrypted at rest and in transit. Guardian doesn\'t collect email or file content, Teams chats or passwords.' },
      ]},
      { type: 'accordion', title: 'Does Guardian send my data to an AI service?', content: [
        { type: 'paragraph', text: 'Yes, to Azure OpenAI Service in iuvo\'s own Azure subscription. Guardian sends configuration and drift details, and the names or sign-in names of the owners of apps Microsoft didn\'t publish, to write drift explanations, app risk summaries and report narratives. Microsoft doesn\'t use those prompts to train its models or share them with OpenAI, and iuvo doesn\'t train any models. Microsoft\'s abuse monitoring can keep prompts it flags for review by its staff.' },
      ]},
      { type: 'heading', level: 2, text: 'Technical', id: 'faq-technical' },
      { type: 'accordion', title: 'What does a "GUARD.IAM.001" policy ID mean?', content: [
        { type: 'paragraph', text: 'Policy IDs follow the pattern GUARD.<SERVICE>.<NUMBER>. "GUARD" is the Guardian prefix, the service code indicates the workload (IAM = Identity, EXO = Exchange, DEF = Defender, TMS = Teams, SPO = SharePoint), and the number is sequential. Example: GUARD.IAM.001 is the first Identity policy.' },
      ]},
      { type: 'accordion', title: 'What are phish-resistant MFA methods?', content: [
        { type: 'paragraph', text: 'Phish-resistant MFA methods are authentication mechanisms immune to common phishing attacks: FIDO2 security keys (e.g., YubiKey), Windows Hello for Business (biometric/PIN tied to TPM), and certificate-based authentication. These bind authentication to the legitimate service, preventing relay or replay attacks.' },
      ]},
      { type: 'accordion', title: 'How do I get help or report issues?', content: [
        { type: 'list', items: [
          'Contact your iuvo Guardian administrator for account and access questions',
          'Email **support@iuvotech.com** for technical support',
          'Include error messages, screenshots, and steps to reproduce when reporting issues',
        ]},
      ]},
    ]
  },
  {
    id: 'changelog',
    title: 'Change Log',
    description: 'History of updates, improvements, and new features.',
    content: [
      { type: 'heading', level: 2, text: 'Version 3.2.0', id: 'v3-2' },
      { type: 'heading', level: 3, text: 'New Features' },
      { type: 'list', items: [
        '**Purview DLP data collection.** Guardian now collects Microsoft Purview Data Loss Prevention policy configurations.',
        '**Exchange audit log collection.** New data source for Exchange Online audit logging configurations.',
        '**Teams policy collection.** Expanded Teams policy data collection for compliance evaluation.',
        '**Expanded policy coverage.** New compliance policies covering additional M365 workload areas.',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 3.1.0 (March 2026)', id: 'v3-1' },
      { type: 'heading', level: 3, text: 'New Features' },
      { type: 'list', items: [
        '**Phish-resistant MFA reporting.** FIDO2, Windows Hello for Business and certificate-based auth tracking.',
        '**User Analytics trends.** Historical charts showing MFA adoption and user activity trends over time.',
        '**Best Practice all controls view.** See every evaluated policy including passing ones, not just violations.',
        '**First-party OAuth exclusion.** Microsoft-owned apps deprioritized in OAuth app risk summaries.',
        '**UI redesign.** Dark theme refresh across User Analytics and Security Center pages.',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 3.0.0 (February 2026)', id: 'v3-0' },
      { type: 'heading', level: 3, text: 'Major Features' },
      { type: 'list', items: [
        '**Standards compliance engine.** 112 policies evaluating against 6 compliance frameworks.',
        '**Security Center hub.** Unified security assessment dashboard with Best Practice Analyzer, Secure Score, SCuBA and App Permissions tabs.',
        '**OAuth app permission monitoring.** AI-enriched risk classification of third-party OAuth applications.',
        '**Executive report generation.** PDF/HTML reports with AI-powered executive summaries.',
        '**User insights expansion.** License utilization, MFA adoption and inactive user detection.',
        '**Self-service signup.** Tenant self-onboarding with admin consent flow.',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 2.6.0 (November 2025)', id: 'v2-6' },
      { type: 'heading', level: 3, text: 'Features & Improvements' },
      { type: 'list', items: [
        '**Drift audit logging.** Complete audit trail for every drift decision, with timestamps and user.',
        '**AI-enhanced severity scoring.** AI detects 7 critical security patterns; MFA weakening scored ≥90% (Critical).',
        '**Enhanced dashboard.** Component/property names in drift titles, severity badges and per-day summaries.',
        '**Performance.** Dashboard loads 99.98% faster (43MB → 10KB payload, 70s → <1s response time).',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 2.5.0 (September 2025)', id: 'v2-5' },
      { type: 'list', items: [
        '**Improved drift detection.** Eliminates duplicate drift detection for complex nested M365 objects.',
        '**Property-level baseline updates.** Granular control with selective property updates in baselines.',
        '**Live dashboard integration.** Real operational data in Recent Activity and Drift Trends.',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 2.4.0 (August 2025)', id: 'v2-4' },
      { type: 'list', items: [
        '**Async scan job tracking.** Immediate job ID with progress polling.',
        '**Batch processing.** Intelligent batching combines multiple acceptances into single baseline updates.',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Earlier Versions', id: 'earlier' },
      { type: 'accordion', title: 'Version 2.0–2.3 (August 2025)', content: [
        { type: 'list', items: [
          '**v2.3.** Professional dashboard with clickable metric cards, URL parameter support and breadcrumb navigation.',
          '**v2.2.** Production drift detection, full status lifecycle, drift reconciliation.',
          '**v2.1.** Multi-tenant architecture, RBAC, Microsoft Entra ID authentication.',
          '**v2.0.** New platform architecture, core drift detection.',
        ]},
      ]},
      { type: 'accordion', title: 'Version 1.0 (July 2025)', content: [
        { type: 'list', items: [
          'Initial release with M365 configuration monitoring',
          'Basic drift detection and baseline management',
          'Manual processing workflows',
        ]},
      ]},
      { type: 'paragraph', text: 'Last Updated: March 2026' },
    ]
  },
]

// ─── Utility: Extract headings from content for "On this page" ──────

function extractHeadings(content: ContentBlock[]): { id: string; text: string; level: number }[] {
  return content
    .filter((block): block is Extract<ContentBlock, { type: 'heading' }> => block.type === 'heading' && block.level === 2 && !!block.id)
    .map(h => ({ id: h.id!, text: h.text, level: h.level }))
}

/** Whether a block (or an accordion's title) mentions the query: accordions holding a match open on search. */
function blockMatches(block: Extract<ContentBlock, { type: 'accordion' }>, query: string): boolean {
  if (query.length < 3) return false
  const q = query.toLowerCase()
  return block.title.toLowerCase().includes(q) || JSON.stringify(block.content).toLowerCase().includes(q)
}

// ─── Utility: Search highlighting ────────────────────────────────────

/** A search match on the selected layer in primary ink: neutral, never a hue. */
function highlightText(text: string, query: string): ReactNode {
  if (!query || query.length < 3) return text
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)
  if (parts.length === 1) return text
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-layer-selected px-0.5 text-text-primary">{part}</mark>
      : part
  )
}

// ─── Content Renderers ───────────────────────────────────────────────

function renderInlineMarkdown(text: string, searchQuery: string): ReactNode {
  // Process bold markers then highlight search matches
  const parts = text.split(/(\*\*.*?\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const inner = part.slice(2, -2)
      return <strong key={i} className="font-semibold text-text-primary">{highlightText(inner, searchQuery)}</strong>
    }
    // Process inline code
    const codeParts = part.split(/(`[^`]+`)/g)
    return codeParts.map((cp, j) => {
      if (cp.startsWith('`') && cp.endsWith('`')) {
        return <CodeSnippet key={`${i}-${j}`} kind="inline" code={cp.slice(1, -1)} />
      }
      return <Fragment key={`${i}-${j}`}>{highlightText(cp, searchQuery)}</Fragment>
    })
  })
}

interface TableRow {
  id: string
  cells: string[]
}

/** A reference table as a Carbon DataTable: the first column is the row's title; phones get stacked rows. */
function HelpTable({ block, label, searchQuery }: { block: Extract<ContentBlock, { type: 'table' }>; label: string; searchQuery: string }) {
  const rows: TableRow[] = block.rows.map((cells, i) => ({ id: String(i), cells }))
  const columns: DataColumn<TableRow>[] = block.headers.map((header, j) => ({
    id: `col-${j}`,
    header,
    primary: j === 0,
    cell: (row) => renderInlineMarkdown(row.cells[j] ?? '', searchQuery),
  }))
  return <DataTable aria-label={label} columns={columns} rows={rows} rowId={(row) => row.id} size="md" />
}

/** The drift lifecycle as condensed-grid tiles: the status, what it means, and the action it asks for. */
function StatusCards({ items }: { items: Extract<ContentBlock, { type: 'status-card' }>['items'] }) {
  return (
    <CondensedGrid as="ul" columns={{ base: 1, 'c-md': 2 }}>
      {items.map((item) => (
        <Tile as="li" key={item.status} className="flex min-w-0 flex-col gap-2">
          <TileHeader title={item.status} level={3} />
          <p className="m-0 text-body text-text-secondary">{item.description}</p>
          <p className="m-0 text-helper text-text-helper">
            <span className="font-semibold text-text-secondary">Action:</span> {item.action}
          </p>
        </Tile>
      ))}
    </CondensedGrid>
  )
}

function FeatureGrid({ items }: { items: { title: string; description: string }[] }) {
  return (
    <CondensedGrid as="ul" columns={{ base: 1, 'c-md': 2, 'c-lg': 3 }}>
      {items.map((item) => (
        <Tile as="li" key={item.title} className="flex min-w-0 flex-col gap-2">
          <TileHeader title={item.title} level={3} />
          <p className="m-0 text-body text-text-secondary">{item.description}</p>
        </Tile>
      ))}
    </CondensedGrid>
  )
}

function ContentRenderer({ block, searchQuery, label }: { block: Exclude<ContentBlock, { type: 'accordion' }>; searchQuery: string; label: string }) {
  switch (block.type) {
    case 'heading':
      if (block.level === 2) {
        return (
          <h2 id={block.id} className="m-0 mt-8 scroll-mt-6 text-heading font-normal text-text-primary first:mt-0">
            {highlightText(block.text, searchQuery)}
          </h2>
        )
      }
      return <h3 className="m-0 mt-4 text-body-lg font-semibold text-text-primary">{highlightText(block.text, searchQuery)}</h3>

    case 'paragraph':
      return <p className="m-0 text-body text-text-secondary">{renderInlineMarkdown(block.text, searchQuery)}</p>

    case 'list':
      return <DocList ordered={block.ordered} items={block.items.map((item) => renderInlineMarkdown(item, searchQuery))} />

    case 'callout':
      return (
        <DocNote tone={block.variant === 'success' ? 'check' : 'info'} title={block.title ? highlightText(block.title, searchQuery) : undefined}>
          <p className="m-0">{renderInlineMarkdown(block.text, searchQuery)}</p>
        </DocNote>
      )

    case 'table':
      return <HelpTable block={block} label={label} searchQuery={searchQuery} />

    case 'status-card':
      return <StatusCards items={block.items} />

    case 'feature-grid':
      return <FeatureGrid items={block.items} />

    case 'divider':
      return <hr className="my-4 border-0 border-t border-line-subtle" />

    default:
      return null
  }
}

/**
 * Render a run of blocks. Consecutive accordion blocks become ONE Carbon
 * Accordion (one rule between rows, not two). A table takes the nearest
 * heading above it as its accessible name. On a search of three or more
 * characters, accordion rows holding a match open (the Accordion remounts
 * per query so its opening set follows the search).
 */
function renderBlocks(blocks: ContentBlock[], searchQuery: string, keyPrefix: string, sectionTitle: string): ReactNode[] {
  const out: ReactNode[] = []
  let lastHeading = sectionTitle
  let run: { block: Extract<ContentBlock, { type: 'accordion' }>; index: number }[] = []

  const flush = () => {
    if (run.length === 0) return
    const items: AccordionItem[] = run.map(({ block, index }) => ({
      id: `${keyPrefix}-${index}`,
      title: highlightText(block.title, searchQuery),
      content: <div className="flex flex-col gap-3">{renderBlocks(block.content, searchQuery, `${keyPrefix}-${index}`, block.title)}</div>,
    }))
    const open = run.filter(({ block }) => blockMatches(block, searchQuery)).map(({ index }) => `${keyPrefix}-${index}`)
    const first = run[0].index
    out.push(
      <Accordion key={`${keyPrefix}-acc-${first}-${searchQuery.length >= 3 ? searchQuery : ''}`} items={items} defaultOpen={open} />,
    )
    run = []
  }

  blocks.forEach((block, i) => {
    if (block.type === 'accordion') {
      run.push({ block, index: i })
      return
    }
    flush()
    if (block.type === 'heading') lastHeading = block.text
    out.push(<ContentRenderer key={`${keyPrefix}-${i}`} block={block} searchQuery={searchQuery} label={lastHeading} />)
  })
  flush()
  return out
}

// ─── Main Help Page ──────────────────────────────────────────────────

/**
 * Ctrl/Cmd+K focuses the search input, Escape blurs it. Returns true when the
 * search term should be cleared (Escape).
 */
export function applySearchShortcut(
  e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'preventDefault'>,
  input: Pick<HTMLInputElement, 'focus' | 'blur'> | null,
): boolean {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    input?.focus()
  }
  if (e.key === 'Escape') {
    input?.blur()
    return true
  }
  return false
}

export default function Help() {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('getting-started')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<string[]>([])
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const contentRef = useRef<HTMLElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Handle URL hash for deep linking
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash) {
      // Check if hash is a section id
      const section = helpSections.find(s => s.id === hash)
      if (section) {
        setActiveSection(hash)
      } else {
        // Hash might be a heading within a section — find the right section
        for (const s of helpSections) {
          const headingMatch = s.content.find(
            (b): b is Extract<ContentBlock, { type: 'heading' }> => b.type === 'heading' && b.id === hash
          )
          if (headingMatch) {
            setActiveSection(s.id)
            // Scroll to heading after render
            requestAnimationFrame(() => {
              document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            })
            break
          }
        }
      }
    }
  }, [location.hash])

  // Search functionality
  useEffect(() => {
    if (searchTerm.length >= 3) {
      const q = searchTerm.toLowerCase()
      const results = helpSections
        .filter(section => {
          const searchable = JSON.stringify(section).toLowerCase()
          return searchable.includes(q)
        })
        .map(section => section.id)
      setSearchResults(results)
      if (results.length > 0 && !results.includes(activeSection)) {
        setActiveSection(results[0])
      }
    } else {
      setSearchResults([])
    }
  }, [searchTerm])

  // Keyboard shortcut: Ctrl/Cmd+K to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (applySearchShortcut(e, searchRef.current)) setSearchTerm('')
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const handleSectionChange = useCallback((id: string) => {
    setActiveSection(id)
    setMobileSidebarOpen(false)
    navigate(`#${id}`, { replace: true })
    // <main> is the scroll container (Layout): bring the new section's top into view.
    contentRef.current?.closest('main')?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [navigate])

  const activeContent = helpSections.find(s => s.id === activeSection)
  const headings = activeContent ? extractHeadings(activeContent.content) : []
  const activeIndex = helpSections.findIndex(s => s.id === activeSection)
  const prev = activeIndex > 0 ? helpSections[activeIndex - 1] : null
  const next = activeIndex < helpSections.length - 1 ? helpSections[activeIndex + 1] : null

  return (
    <div className="mt-8 grid grid-cols-1 gap-8 c-lg:grid-cols-[256px_minmax(0,1fr)]">
      {/* Search and the section list */}
      <aside aria-label="Help navigation" className="min-w-0 c-lg:sticky c-lg:top-0 c-lg:self-start">
        <TableSearch label="Search help" value={searchTerm} onChange={setSearchTerm} inputRef={searchRef} />
        {searchTerm.length >= 3 && (
          <p role="status" className="m-0 mt-2 text-helper text-text-helper">
            {searchResults.length} section{searchResults.length !== 1 ? 's' : ''} matching &ldquo;{searchTerm}&rdquo;
          </p>
        )}

        <Button
          kind="ghost"
          size="md"
          fullWidth
          className="mt-4 c-lg:hidden"
          aria-expanded={mobileSidebarOpen}
          aria-controls="help-sections"
          onClick={() => setMobileSidebarOpen(open => !open)}
          icon={<IconChevronDown className={clsx('transition-transform duration-150 ease-carbon', mobileSidebarOpen && 'rotate-180')} />}
        >
          {mobileSidebarOpen ? 'Hide sections' : 'Browse sections'}
        </Button>

        <nav id="help-sections" aria-label="Help sections" className={clsx('mt-4', !mobileSidebarOpen && 'max-c-lg:hidden')}>
          <ul className="m-0 list-none p-0">
            {helpSections.map((section) => {
              const isActive = activeSection === section.id
              const isFiltered = searchResults.length > 0 && !searchResults.includes(section.id)
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => handleSectionChange(section.id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={clsx(
                      'relative flex min-h-8 w-full items-center px-4 py-1.5 text-left text-body font-semibold leading-[1.29] transition-colors duration-110 ease-carbon hover:bg-layer-2 hover:text-text-primary max-c-lg:min-h-10',
                      isActive
                        ? 'bg-layer-2 text-text-primary before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-interactive-line'
                        : isFiltered
                          ? 'text-text-placeholder'
                          : 'text-text-secondary',
                    )}
                  >
                    {section.title}
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>
      </aside>

      {/* The active section */}
      <article ref={contentRef} aria-labelledby="help-section-title" className="min-w-0 max-w-[56rem]">
        {activeContent && (
          <>
            <h1 id="help-section-title" className="m-0 text-balance text-headline-sm font-light tracking-normal text-text-primary max-c-md:text-[2rem]">
              {highlightText(activeContent.title, searchTerm)}
            </h1>
            <p className="m-0 mt-4 max-w-[56ch] text-heading font-light text-text-secondary max-c-md:text-[1.125rem]">
              {activeContent.description}
            </p>

            {/* "On this page" quick links */}
            {headings.length > 2 && (
              <nav aria-label="On this page" className="mt-6">
                <p className="m-0 text-helper text-text-helper">On this page</p>
                <ul className="m-0 mt-2 flex list-none flex-wrap gap-x-6 gap-y-1 p-0">
                  {headings.map(h => (
                    <li key={h.id}>
                      <a
                        href={`#${h.id}`}
                        className="text-body text-link hover:text-link-hover hover:underline"
                        onClick={(e) => {
                          e.preventDefault()
                          document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }}
                      >
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            {/* Content blocks */}
            <div className="mt-8 flex flex-col gap-4">
              {renderBlocks(activeContent.content, searchTerm, activeContent.id, activeContent.title)}
            </div>

            {/* Previous and next sections */}
            <nav aria-label="Help pages" className="mt-12 flex flex-wrap justify-between gap-4 border-t border-line-subtle pt-6">
              {prev ? (
                <Button kind="ghost" onClick={() => handleSectionChange(prev.id)}>
                  Back to {prev.title}
                </Button>
              ) : <span />}
              {next ? (
                <Button kind="ghost" icon={<IconArrowRight />} onClick={() => handleSectionChange(next.id)}>
                  Next: {next.title}
                </Button>
              ) : <span />}
            </nav>

            {/* Contact */}
            <CondensedGrid columns={{ base: 1, 'c-md': 2 }} className="mt-6">
              <Tile className="flex min-w-0 flex-col gap-1">
                <TileHeader title="Still need help?" />
                <p className="m-0 text-helper text-text-helper">Contact your administrator or reach out to support</p>
                <a href="mailto:support@iuvotech.com" className="mt-3 w-fit text-body text-link hover:text-link-hover hover:underline">
                  Email support
                </a>
              </Tile>
              <Tile className="flex min-w-0 flex-col gap-1">
                <TileHeader title="Feature request or bug?" />
                <p className="m-0 text-helper text-text-helper">Submit feedback to help us improve Guardian</p>
                <ButtonLink
                  href="https://github.com/iuvonauts/guardian-feedback/issues"
                  kind="ghost"
                  size="sm"
                  icon={<IconExternal />}
                  className="-ml-3 mt-2 w-fit"
                >
                  Send feedback
                </ButtonLink>
              </Tile>
            </CondensedGrid>
          </>
        )}
      </article>
    </div>
  )
}
